import React from 'react';
import { Gauge, Volume2, VolumeX, Maximize, Blend, Clock } from 'lucide-react';
import {
  BrightnessCurve,
  ClipFit,
  ClipMotion,
  ClipTransition,
  DEFAULT_BRIGHTNESS_CURVE_SECONDS,
  EditorSegment,
  formatTime,
} from '../../types/editor';

const SPEEDS = [0.5, 1, 1.5, 2];
const FITS: { id: ClipFit; label: string }[] = [
  { id: 'contain', label: 'Encaixar' },
  { id: 'cover', label: 'Preencher' },
];
const TRANSITIONS: { id: ClipTransition; label: string }[] = [
  { id: 'none', label: 'Corte' },
  { id: 'dissolve', label: 'Dissolve' },
  { id: 'fade', label: 'Fade' },
];
const BRIGHTNESS_CURVES: { id: BrightnessCurve; label: string }[] = [
  { id: 'none', label: 'Off' },
  { id: 'in', label: 'Entrada' },
  { id: 'out', label: 'Saída' },
  { id: 'both', label: 'Pontas' },
];
const MOTIONS: { id: ClipMotion; label: string }[] = [
  { id: 'none', label: 'Parado' },
  { id: 'zoom-in', label: 'Zoom +' },
  { id: 'zoom-out', label: 'Zoom −' },
  { id: 'pan-left', label: 'Pan ←' },
  { id: 'pan-right', label: 'Pan →' },
];

interface Props {
  segment: EditorSegment | null;
  onUpdate: (id: string, updates: Partial<EditorSegment>) => void;
  onSpeedChange: (speed: number) => void;
}

export default function ClipInspector({ segment, onUpdate, onSpeedChange }: Props) {
  if (!segment) {
    return (
      <div style={{ padding: 20, textAlign: 'center', fontSize: '0.7rem', color: 'rgba(255,255,255,0.3)' }}>
        Selecione um clip na timeline
      </div>
    );
  }

  const isStill = segment.type === 'image';

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 12 }}>
      <div>
        <label style={labelStyle}>Nome</label>
        <input
          value={segment.label}
          onChange={(e) => onUpdate(segment.id, { label: e.target.value })}
          style={inputStyle}
        />
      </div>

      <div style={{ fontSize: '0.6rem', color: 'rgba(255,255,255,0.4)', display: 'flex', gap: 8 }}>
        <span>{formatTime(segment.duration)}</span>
        <span>{segment.speed}x</span>
      </div>

      {isStill && (
        <div>
          <label style={labelStyle}>
            <Clock size={10} /> Duração
          </label>
          <input
            type="number"
            min={0.5}
            max={120}
            step={0.5}
            value={segment.duration}
            onChange={(e) => {
              const duration = Math.max(0.5, parseFloat(e.target.value) || 0.5);
              onUpdate(segment.id, { duration, originalDuration: duration });
            }}
            style={inputStyle}
          />
        </div>
      )}

      <div>
        <label style={labelStyle}>
          <Gauge size={10} /> Velocidade
        </label>
        <div style={chipRow}>
          {SPEEDS.map((s) => (
            <button
              key={s}
              onClick={() => onSpeedChange(s)}
              style={chip(segment.speed === s)}
            >
              {s}x
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>
          <Maximize size={10} /> Enquadramento
        </label>
        <div style={chipRow}>
          {FITS.map((f) => (
            <button
              key={f.id}
              onClick={() => onUpdate(segment.id, { fit: f.id })}
              style={chip(segment.fit === f.id)}
            >
              {f.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>
          Movimento
        </label>
        <div style={chipRow}>
          {MOTIONS.map((m) => (
            <button
              key={m.id}
              onClick={() => onUpdate(segment.id, { motion: m.id })}
              style={chip((segment.motion || 'none') === m.id)}
            >
              {m.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>
          Curva de brilho
        </label>
        <div style={chipRow}>
          {BRIGHTNESS_CURVES.map((c) => (
            <button
              key={c.id}
              onClick={() => onUpdate(segment.id, { brightnessCurve: c.id })}
              style={chip((segment.brightnessCurve || 'none') === c.id)}
            >
              {c.label}
            </button>
          ))}
        </div>
        {(segment.brightnessCurve || 'none') !== 'none' && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <input
              type="range"
              min={0.2}
              max={1.5}
              step={0.05}
              value={segment.brightnessCurveSeconds || DEFAULT_BRIGHTNESS_CURVE_SECONDS}
              onChange={(e) => onUpdate(segment.id, { brightnessCurveSeconds: parseFloat(e.target.value) })}
              style={{ flex: 1, accentColor: '#6366f1', height: 3 }}
            />
            <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)', minWidth: 36 }}>
              {(segment.brightnessCurveSeconds || DEFAULT_BRIGHTNESS_CURVE_SECONDS).toFixed(2)}s
            </span>
          </div>
        )}
      </div>

      <div>
        <label style={labelStyle}>
          <Blend size={10} /> Transição de saída
        </label>
        <div style={chipRow}>
          {TRANSITIONS.map((t) => (
            <button
              key={t.id}
              onClick={() => onUpdate(segment.id, { transitionOut: t.id })}
              style={chip(segment.transitionOut === t.id)}
            >
              {t.label}
            </button>
          ))}
        </div>
      </div>

      <div>
        <label style={labelStyle}>
          {segment.muted ? <VolumeX size={10} /> : <Volume2 size={10} />} Volume
        </label>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <input
            type="range"
            min={0}
            max={1}
            step={0.05}
            value={segment.muted ? 0 : segment.volume}
            onChange={(e) => onUpdate(segment.id, { volume: parseFloat(e.target.value), muted: false })}
            style={{ flex: 1, accentColor: '#6366f1', height: 3 }}
          />
          <span style={{ fontSize: '0.58rem', color: 'rgba(255,255,255,0.4)', minWidth: 28 }}>
            {segment.muted ? 'off' : `${Math.round(segment.volume * 100)}%`}
          </span>
          <button
            onClick={() => onUpdate(segment.id, { muted: !segment.muted })}
            style={chip(segment.muted)}
            title="Mudo"
          >
            {segment.muted ? <VolumeX size={11} /> : <Volume2 size={11} />}
          </button>
        </div>
      </div>
    </div>
  );
}

const labelStyle: React.CSSProperties = {
  display: 'flex', alignItems: 'center', gap: 4,
  fontSize: '0.62rem', color: 'rgba(255,255,255,0.45)', marginBottom: 5,
};

const inputStyle: React.CSSProperties = {
  width: '100%', boxSizing: 'border-box',
  background: 'rgba(0,0,0,0.3)', border: '1px solid rgba(255,255,255,0.08)',
  borderRadius: 5, padding: '5px 8px', color: '#fff', fontSize: '0.7rem',
  fontFamily: 'inherit',
};

const chipRow: React.CSSProperties = { display: 'flex', gap: 4, flexWrap: 'wrap' };

const chip = (active: boolean): React.CSSProperties => ({
  padding: '3px 8px', borderRadius: 4, border: 'none',
  fontSize: '0.6rem', fontFamily: 'inherit',
  background: active ? 'rgba(99,102,241,0.25)' : 'rgba(255,255,255,0.05)',
  color: active ? '#c7d2fe' : 'rgba(255,255,255,0.45)',
  cursor: 'pointer',
});
